import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Image as ImageIcon, X, ChevronUp, ChevronDown, GripVertical } from 'lucide-react';

export const MAX_STORY_IMAGES = 6;

export function countStoryImages(editor) {
  if (!editor) return 0;
  let n = 0;
  editor.state.doc.descendants(node => {
    if (node.type.name === 'storyImage') n += 1;
  });
  return n;
}

// ─── Node view ───

function StoryImageView({ node, updateAttributes, deleteNode, editor, getPos, extension }) {
  const { src, alt, caption, credit, width, height } = node.attrs;

  const pos = () => (typeof getPos === 'function' ? getPos() : getPos);

  const moveBy = (offset) => {
    const from = pos();
    if (typeof from !== 'number') return;
    const { state, view } = editor;
    const $from = state.doc.resolve(from);
    const parent = $from.parent;
    const index = $from.index();
    const target = index + offset;
    if (target < 0 || target >= parent.childCount) return;

    const current = parent.child(index);
    const sibling = parent.child(target);
    const insertAt = offset < 0 ? from - sibling.nodeSize : from + sibling.nodeSize;

    const tr = state.tr;
    tr.delete(from, from + current.nodeSize);
    tr.insert(insertAt, current);
    view.dispatch(tr);
  };

  const requestReplace = () => extension.options.onRequestReplace?.(pos());

  const btn = 'p-1 rounded text-[#6E6258] hover:bg-[#FFE4C4] hover:text-[#1F1A17] transition-colors disabled:opacity-30 disabled:hover:bg-transparent';
  const field = 'w-full h-7 px-2 text-xs bg-white border border-border rounded text-[#1F1A17] placeholder:text-[#9C8E84] focus:outline-none focus:border-[#1F1A17]';
  const flabel = 'text-[10px] text-[#9C8E84] mb-0.5 block';

  const stop = {
    onMouseDown: e => e.stopPropagation(),
    onKeyDown: e => e.stopPropagation(),
  };

  return (
    <NodeViewWrapper className="story-image-node" contentEditable={false}>
      <div className="story-image-node__bar">
        <span className="story-image-node__grip" data-drag-handle title="Drag to move">
          <GripVertical size={13} />
        </span>
        <span className="text-[10px] text-[#9C8E84] mr-auto">Image</span>
        <button type="button" onClick={() => moveBy(-1)} className={btn} title="Move up">
          <ChevronUp size={14} />
        </button>
        <button type="button" onClick={() => moveBy(1)} className={btn} title="Move down">
          <ChevronDown size={14} />
        </button>
        <button type="button" onClick={requestReplace} className={btn} title="Replace image">
          <ImageIcon size={14} />
        </button>
        <button
          type="button"
          onClick={deleteNode}
          className="p-1 rounded text-[#9C8E84] hover:bg-red-50 hover:text-red-500 transition-colors"
          title="Remove image"
        >
          <X size={14} />
        </button>
      </div>

      {src
        ? <img src={src} alt={alt || ''} width={width || undefined} height={height || undefined} className="story-image-node__img" />
        : (
          <button type="button" onClick={requestReplace} className="story-image-node__empty">
            <ImageIcon size={22} className="text-[#C9B49A] mb-1.5" />
            <span className="text-xs text-muted-foreground">Select an image</span>
          </button>
        )
      }

      <div className="story-image-node__fields" {...stop}>
        <div className="col-span-2">
          <label className={flabel}>Caption</label>
          <input
            value={caption || ''}
            onChange={e => updateAttributes({ caption: e.target.value })}
            placeholder="What the picture shows"
            className={field}
          />
        </div>
        <div>
          <label className={flabel}>Credit</label>
          <input
            value={credit || ''}
            onChange={e => updateAttributes({ credit: e.target.value })}
            placeholder="Photo credit"
            className={field}
          />
        </div>
        <div>
          <label className={flabel}>Alt text</label>
          <input
            value={alt || ''}
            onChange={e => updateAttributes({ alt: e.target.value })}
            placeholder="Describe for screen readers"
            className={field}
          />
        </div>
      </div>
    </NodeViewWrapper>
  );
}

// ─── Extension ───

const StoryImage = Node.create({
  name: 'storyImage',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addOptions() {
    return { onRequestReplace: null };
  },

  addAttributes() {
    return {
      src:      { default: '' },
      alt:      { default: '' },
      caption:  { default: '' },
      credit:   { default: '' },
      assetId:  { default: null },
      width:    { default: null },
      height:   { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'figure[data-story-image]',
        priority: 60,
        getAttrs: (el) => {
          const img = el.querySelector('img');
          return {
            src:     img?.getAttribute('src') || '',
            alt:     img?.getAttribute('alt') || '',
            width:   img?.getAttribute('width') || null,
            height:  img?.getAttribute('height') || null,
            assetId: el.getAttribute('data-asset-id') || null,
            caption: el.querySelector('.story-image-caption')?.textContent || '',
            credit:  el.querySelector('.story-image-credit')?.textContent || '',
          };
        },
      },
      {
        tag: 'img[src]',
        priority: 40,
        getAttrs: (el) => ({
          src:    el.getAttribute('src') || '',
          alt:    el.getAttribute('alt') || '',
          width:  el.getAttribute('width') || null,
          height: el.getAttribute('height') || null,
        }),
      },
    ];
  },

  renderHTML({ node }) {
    const { src, alt, caption, credit, assetId, width, height } = node.attrs;

    const figureAttrs = { 'data-story-image': '' };
    if (assetId) figureAttrs['data-asset-id'] = assetId;

    const imgAttrs = { src, alt: alt || '' };
    if (width)  imgAttrs.width  = width;
    if (height) imgAttrs.height = height;

    const children = [['img', imgAttrs]];

    if (caption || credit) {
      const parts = [];
      if (caption) parts.push(['span', { class: 'story-image-caption' }, caption]);
      if (credit)  parts.push(['span', { class: 'story-image-credit' }, credit]);
      children.push(['figcaption', {}, ...parts]);
    }

    return ['figure', mergeAttributes(figureAttrs), ...children];
  },

  addNodeView() {
    return ReactNodeViewRenderer(StoryImageView);
  },
});

export default StoryImage;